/**
 * Turning a picked `File` into something the API accepts.
 *
 * The checks here duplicate the Worker's, and the duplication is the point: the
 * server's copy is the one that decides, and this one exists so a customer who
 * picked a 40 MB photo finds out instantly instead of after a 40 MB upload on
 * hotel wifi. Nothing here is trusted server-side — `support_attachments.ts`
 * sniffs the magic bytes and ignores everything the browser claimed.
 *
 * base64 in a JSON body rather than multipart, because it keeps one content
 * type across every write in this API and the sizes involved are small. It
 * costs a third in transfer, which for a 2 MB screenshot is not the thing worth
 * optimising on a support form.
 */

import type { OutgoingAttachment } from "@/lib/api";
import { MAX_ATTACHMENTS_PER_MESSAGE, MAX_ATTACHMENT_BYTES } from "../../shared/support";

/** What the picker holds: the payload plus what the UI needs to preview it. */
export type PickedAttachment = OutgoingAttachment & {
  /** Stable key for React, and the handle the remove button uses. */
  id: string;
  sizeBytes: number;
  /** Object URL for the thumbnail. Revoke it when the item goes away. */
  previewUrl: string;
};

export type PickResult =
  | { ok: true; attachments: PickedAttachment[] }
  | { ok: false; error: string };

/**
 * The types the file input offers and this function accepts.
 *
 * Advisory only — a browser will happily hand over anything, and the server
 * decides from the bytes. Listing them keeps the OS file dialog from showing a
 * customer a folder of PDFs it will then reject.
 */
export const ACCEPTED_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export async function pickAttachments(
  files: FileList | File[],
  existingCount: number,
): Promise<PickResult> {
  const list = Array.from(files);
  if (list.length === 0) return { ok: true, attachments: [] };

  if (existingCount + list.length > MAX_ATTACHMENTS_PER_MESSAGE) {
    return {
      ok: false,
      error: `Up to ${MAX_ATTACHMENTS_PER_MESSAGE} images per message.`,
    };
  }

  const attachments: PickedAttachment[] = [];
  for (const file of list) {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return { ok: false, error: `${file.name} is over 10 MB. Try a smaller image.` };
    }
    if (!file.type.startsWith("image/")) {
      return { ok: false, error: `${file.name} is not an image.` };
    }
    attachments.push({
      id: crypto.randomUUID(),
      filename: file.name,
      data_base64: await fileToBase64(file),
      sizeBytes: file.size,
      previewUrl: URL.createObjectURL(file),
    });
  }
  return { ok: true, attachments };
}

/**
 * Read a File to base64.
 *
 * `FileReader` rather than looping over the bytes: a 10 MB image is 10 million
 * `String.fromCharCode` calls, which locks the main thread long enough to be
 * visible. The `data:` prefix is left on — the server strips it, and keeping it
 * means the same string works directly as an <img src> for the preview if the
 * object URL ever needs replacing.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/** Release the object URLs behind a set of picks. */
export function releasePreviews(attachments: PickedAttachment[]): void {
  for (const a of attachments) URL.revokeObjectURL(a.previewUrl);
}

/** `1.4 MB`. For the row under a thumbnail. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** What the API wants — the preview fields are local to the picker. */
export function toOutgoing(attachments: PickedAttachment[]): OutgoingAttachment[] {
  return attachments.map((a) => ({ filename: a.filename, data_base64: a.data_base64 }));
}
